import { FEATURE_FLAGS, REDIS_FEATURE_FLAGS } from '../../../../featureFlags';
import { frontendURL } from '../../../../helper/URLHelper';
import {
  lockedFeatureRouteName,
  lockedFeatureURL,
} from '../../../../constants/lockedFeatures';

const SECTION_CLAUDIA = 'SECTION_CLAUDIA';
const SECTION_SERVICE_GENERAL = 'SECTION_SERVICE_GENERAL';
const SECTION_SERVICE_DETAIL = 'SECTION_SERVICE_DETAIL';
const SECTION_REPORT_BUILDER = 'SECTION_REPORT_BUILDER';

// Claudia-only accounts keep the reports they already have and get a single
// entry for everything else, pointing at the upsell page.
// `lock-closed`, not `lock-alt`: the secondary nav renders through
// `fluent-icon` (v1 pack only), and an icon missing from that pack throws in
// Icon.vue#pathSource, taking the whole menu down with it.
const advancedReportsUpsellItem = accountId => ({
  icon: 'lock-closed',
  label: 'REPORTS_ADVANCED_LOCKED',
  key: 'reports-advanced-locked',
  hasSubMenu: false,
  toState: frontendURL(lockedFeatureURL(accountId, 'reports')),
  toStateName: lockedFeatureRouteName('reports'),
});

// Picks the Omni or legacy (Explo) variant of a report nav field. Extracted so
// the per-item branching doesn't inflate the builder's cognitive complexity
// (Sonar S3776) — a function call doesn't count toward it the way a `?:` does.
const omniOrLegacy = (useOmni, omni, legacy) => (useOmni ? omni : legacy);

const claudiaReport = (
  accountId,
  { icon, iconType, label, key, path, toStateName }
) => ({
  ...(iconType ? { iconType } : {}),
  icon,
  label,
  hasSubMenu: false,
  section: SECTION_CLAUDIA,
  // Only accounts with ClaudIA enabled (Redis flag ENABLE_CLAUDIA -> the
  // `claudia` account feature) see this group.
  featureFlag: REDIS_FEATURE_FLAGS.CLAUDIA,
  key,
  toState: frontendURL(`accounts/${accountId}/reports/${path}`),
  toStateName,
});

const reports = (accountId, flags = {}, claudiaOnly = false) => {
  const routes = [
    'omni_claudia_general_info_report',
    'omni_claudia_quality_report',
    'omni_claudia_tags_topics_report',
    'omni_claudia_contents_report',
    'omni_claudia_controlled_flows_report',
    'omni_claudia_agentic_report',
    'omni_claudia_audit_report',
    'omni_claudia_other_indicators_report',
    'claudia_metricas_legado_report',
    'account_overview_reports',
    'advanced_monitoring',
    'conversation_reports',
    'omni_conversations_report',
    'csat_reports',
    'omni_csat_report',
    'bot_reports',
    'agent_reports',
    'omni_agents_report',
    'label_reports',
    'inbox_reports',
    'team_reports',
    'omni_teams_report',
    'reports_team_view',
    'reports_agent_view',
    'ticket_metrics_reports',
    'sla_reports',
    'omni_sla_policies_report',
    'reports_dashboard',
    'reports_report_builder',
    'reports_create_mode',
    'service_time_metrics_reports',
    'omni_service_time_metrics_report',
    'campaign_metrics_reports',
    'omni_campaign_metrics_report',
  ];

  const menuItems = [
    {
      icon: 'arrow-trending-lines',
      label: 'REPORTS_OVERVIEW',
      hasSubMenu: false,
      section: SECTION_SERVICE_GENERAL,
      toState: frontendURL(`accounts/${accountId}/reports/overview`),
      toStateName: 'account_overview_reports',
    },
    {
      icon: 'data-histogram',
      label: 'ADVANCED_MONITORING',
      hasSubMenu: false,
      section: SECTION_SERVICE_GENERAL,
      toState: frontendURL(`accounts/${accountId}/reports/overview-2`),
      toStateName: 'advanced_monitoring',
    },
    {
      icon: 'chat',
      label: 'REPORTS_CONVERSATION',
      hasSubMenu: false,
      section: SECTION_SERVICE_GENERAL,
      toState: omniOrLegacy(
        flags.omniConversationsReport,
        frontendURL(`accounts/${accountId}/reports/conversations-omni`),
        frontendURL(`accounts/${accountId}/reports/conversation`)
      ),
      toStateName: omniOrLegacy(
        flags.omniConversationsReport,
        'omni_conversations_report',
        'conversation_reports'
      ),
    },
    // Reports do Omni usam feature flag GLOBAL (Redis, gerenciada em
    // /super_admin/feature_flags). Os reports antigos do Explo eram gateados
    // por flag PER-ACCOUNT (custom_attributes['feature_flags']['explo'][...]).
    // Quando a flag global Omni está ON, dropamos o gate per-account: todas
    // as contas veem o item apontando pra rota Omni. Quando OFF, voltamos ao
    // fluxo padrão e o gate per-account decide a visibilidade.
    {
      icon: 'alarm',
      label: 'REPORTS_SERVICE_TIME_METRICS',
      hasSubMenu: false,
      section: SECTION_SERVICE_GENERAL,
      toState: omniOrLegacy(
        flags.omniServiceTimeMetricsReport,
        frontendURL(`accounts/${accountId}/reports/service-time-metrics-omni`),
        frontendURL(`accounts/${accountId}/reports/service-time-metrics`)
      ),
      toStateName: omniOrLegacy(
        flags.omniServiceTimeMetricsReport,
        'omni_service_time_metrics_report',
        'service_time_metrics_reports'
      ),
      ...omniOrLegacy(
        flags.omniServiceTimeMetricsReport,
        {},
        {
          newFeatureFlag: 'explo.reports_dashboard',
        }
      ),
    },
    {
      icon: 'megaphone',
      label: 'REPORTS_CAMPAIGN_METRICS',
      hasSubMenu: false,
      section: SECTION_SERVICE_GENERAL,
      toState: omniOrLegacy(
        flags.omniCampaignMetricsReport,
        frontendURL(`accounts/${accountId}/reports/campaign-metrics-omni`),
        frontendURL(`accounts/${accountId}/reports/campaign-metrics`)
      ),
      toStateName: omniOrLegacy(
        flags.omniCampaignMetricsReport,
        'omni_campaign_metrics_report',
        'campaign_metrics_reports'
      ),
      ...omniOrLegacy(
        flags.omniCampaignMetricsReport,
        {},
        {
          newFeatureFlag: 'explo.reports_dashboard',
        }
      ),
    },
    {
      icon: 'emoji',
      label: 'CSAT',
      hasSubMenu: false,
      section: SECTION_SERVICE_GENERAL,
      toState: omniOrLegacy(
        flags.omniCsatReport,
        frontendURL(`accounts/${accountId}/reports/csat-omni`),
        frontendURL(`accounts/${accountId}/reports/csat`)
      ),
      toStateName: omniOrLegacy(
        flags.omniCsatReport,
        'omni_csat_report',
        'csat_reports'
      ),
    },
    {
      icon: 'bot',
      label: 'REPORTS_BOT',
      hasSubMenu: false,
      section: SECTION_SERVICE_DETAIL,
      featureFlag: FEATURE_FLAGS.RESPONSE_BOT,
      toState: frontendURL(`accounts/${accountId}/reports/bot`),
      toStateName: 'bot_reports',
    },
    {
      icon: 'people',
      label: 'REPORTS_AGENT',
      hasSubMenu: false,
      section: SECTION_SERVICE_DETAIL,
      toState: omniOrLegacy(
        flags.omniAgentsReport,
        frontendURL(`accounts/${accountId}/reports/agents-omni`),
        frontendURL(`accounts/${accountId}/reports/agent`)
      ),
      toStateName: omniOrLegacy(
        flags.omniAgentsReport,
        'omni_agents_report',
        'agent_reports'
      ),
      ...omniOrLegacy(
        flags.omniAgentsReport,
        {},
        {
          newFeatureFlag: 'explo.reports_dashboard',
          hideIfFeatureFlag: true,
        }
      ),
    },
    {
      icon: 'tag',
      label: 'REPORTS_LABEL',
      hasSubMenu: false,
      section: SECTION_SERVICE_DETAIL,
      toState: frontendURL(`accounts/${accountId}/reports/label`),
      toStateName: 'label_reports',
      newFeatureFlag: 'explo.reports_dashboard',
      hideIfFeatureFlag: true,
    },
    {
      icon: 'mail-inbox-all',
      label: 'REPORTS_INBOX',
      hasSubMenu: false,
      section: SECTION_SERVICE_DETAIL,
      toState: frontendURL(`accounts/${accountId}/reports/inboxes`),
      toStateName: 'inbox_reports',
      newFeatureFlag: 'explo.reports_dashboard',
      hideIfFeatureFlag: true,
    },
    {
      icon: 'people-team',
      label: 'REPORTS_TEAM',
      hasSubMenu: false,
      section: SECTION_SERVICE_DETAIL,
      toState: omniOrLegacy(
        flags.omniTeamsReport,
        frontendURL(`accounts/${accountId}/reports/teams-omni`),
        frontendURL(`accounts/${accountId}/reports/teams`)
      ),
      toStateName: omniOrLegacy(
        flags.omniTeamsReport,
        'omni_teams_report',
        'team_reports'
      ),
      ...omniOrLegacy(
        flags.omniTeamsReport,
        {},
        {
          newFeatureFlag: 'explo.reports_dashboard',
          hideIfFeatureFlag: true,
        }
      ),
    },
    // When the global Omni flag is ON, hide the Explo variant (which would otherwise
    // show for accounts with `explo.reports_dashboard` enabled) — the Omni item above already covers it.
    ...omniOrLegacy(
      flags.omniTeamsReport,
      [],
      [
        {
          icon: 'people-team',
          label: 'REPORTS_TEAM',
          hasSubMenu: false,
          section: SECTION_SERVICE_DETAIL,
          toState: frontendURL(`accounts/${accountId}/reports/team-view`),
          toStateName: 'reports_team_view',
          newFeatureFlag: 'explo.reports_dashboard',
        },
      ]
    ),
    ...omniOrLegacy(
      flags.omniAgentsReport,
      [],
      [
        {
          icon: 'people-team',
          label: 'REPORTS_AGENT',
          hasSubMenu: false,
          section: SECTION_SERVICE_DETAIL,
          toState: frontendURL(`accounts/${accountId}/reports/agent-view`),
          toStateName: 'reports_agent_view',
          newFeatureFlag: 'explo.reports_dashboard',
        },
      ]
    ),
    {
      icon: 'document-list-clock',
      label: 'REPORTS_SLA',
      hasSubMenu: false,
      section: SECTION_SERVICE_DETAIL,
      featureFlag: FEATURE_FLAGS.SLA,
      toState: frontendURL(`accounts/${accountId}/reports/sla`),
      toStateName: 'sla_reports',
    },
    {
      icon: 'document-list-clock',
      label: 'REPORTS_SLA_POLICIES',
      hasSubMenu: false,
      section: SECTION_SERVICE_DETAIL,
      // Same gate as the report above: an account without SLA has nothing to
      // show here.
      featureFlag: FEATURE_FLAGS.SLA,
      toState: frontendURL(`accounts/${accountId}/reports/sla-policies-omni`),
      toStateName: 'omni_sla_policies_report',
    },
    ...[
      {
        icon: 'cloud',
        label: 'REPORTS_GENERAL_INFO',
        key: 'claudia-general-info',
        path: 'claudia-general-info-omni',
        toStateName: 'omni_claudia_general_info_report',
      },
      {
        icon: 'star-emphasis',
        label: 'REPORTS_CLAUDIA_QUALITY',
        key: 'claudia-quality',
        path: 'claudia-quality-omni',
        toStateName: 'omni_claudia_quality_report',
      },
      {
        icon: 'tag',
        label: 'REPORTS_CLAUDIA_TAGS_TOPICS',
        key: 'claudia-tags-topics',
        path: 'claudia-tags-topics-omni',
        toStateName: 'omni_claudia_tags_topics_report',
      },
      {
        icon: 'library',
        label: 'REPORTS_CLAUDIA_CONTENTS',
        key: 'claudia-contents',
        path: 'claudia-contents-omni',
        toStateName: 'omni_claudia_contents_report',
      },
      {
        iconType: 'claudia',
        icon: 'controlled-flows',
        label: 'REPORTS_CLAUDIA_CONTROLLED_FLOWS',
        key: 'claudia-controlled-flows',
        path: 'claudia-controlled-flows-omni',
        toStateName: 'omni_claudia_controlled_flows_report',
      },
      {
        icon: 'sparkle',
        label: 'REPORTS_CLAUDIA_AGENTIC',
        key: 'claudia-agentic',
        path: 'claudia-agentic-omni',
        toStateName: 'omni_claudia_agentic_report',
      },
      {
        icon: 'search-check',
        label: 'REPORTS_CLAUDIA_AUDIT',
        key: 'claudia-audit',
        path: 'claudia-audit-omni',
        toStateName: 'omni_claudia_audit_report',
      },
      {
        icon: 'more-horizontal',
        label: 'REPORTS_CLAUDIA_OTHER_INDICATORS',
        key: 'claudia-other-indicators',
        path: 'claudia-other-indicators-omni',
        toStateName: 'omni_claudia_other_indicators_report',
      },
      {
        icon: 'archive',
        label: 'REPORTS_CLAUDIA_METRICAS_LEGADO',
        key: 'claudia-metricas-legado',
        path: 'claudia-metricas-legado',
        toStateName: 'claudia_metricas_legado_report',
      },
    ].map(report => claudiaReport(accountId, report)),
    {
      icon: 'arrow-download',
      label: 'REPORTS_TICKET_METRICS',
      hasSubMenu: false,
      section: SECTION_REPORT_BUILDER,
      pinToBottom: true,
      // Hidden by default; gated behind the global Redis flag
      // ENABLE_REPORTS_TICKET_METRICS (managed in /super_admin/feature_flags).
      featureFlag: REDIS_FEATURE_FLAGS.REPORTS_TICKET_METRICS,
      toState: frontendURL(`accounts/${accountId}/reports/ticket_metrics`),
      toStateName: 'ticket_metrics_reports',
    },
    {
      icon: 'arrow-trending-lines',
      label: 'REPORT_BUILDER_LEGACY',
      hasSubMenu: false,
      section: SECTION_REPORT_BUILDER,
      pinToBottom: true,
      toState: frontendURL(`accounts/${accountId}/reports/report-builder`),
      toStateName: 'reports_report_builder',
      newFeatureFlag: 'explo.reports_report_builder',
      allowedRolesField: 'report_builder_legacy_allowed_roles',
    },
    {
      icon: 'graph-alt-2',
      label: 'CREATE_MODE',
      hasSubMenu: false,
      section: SECTION_REPORT_BUILDER,
      pinToBottom: true,
      toState: frontendURL(`accounts/${accountId}/reports/create-mode`),
      toStateName: 'reports_create_mode',
    },
  ];

  return {
    parentNav: 'reports',
    // In Claudia-only mode the Reports section collapses to the Omni report
    // builder plus the ClaudIA group — filter both `routes` and the secondary
    // nav so the intent is explicit in the data, not just enforced by the
    // route guard.
    routes: claudiaOnly
      ? [
          'reports_create_mode',
          'omni_claudia_general_info_report',
          'omni_claudia_quality_report',
          'omni_claudia_tags_topics_report',
          'omni_claudia_contents_report',
          'omni_claudia_controlled_flows_report',
          'omni_claudia_agentic_report',
          'omni_claudia_audit_report',
          'omni_claudia_other_indicators_report',
          'claudia_metricas_legado_report',
          lockedFeatureRouteName('reports'),
        ]
      : routes,
    menuItems: claudiaOnly
      ? [
          ...menuItems.filter(
            item =>
              item.toStateName === 'reports_create_mode' ||
              item.section === SECTION_CLAUDIA
          ),
          advancedReportsUpsellItem(accountId),
        ]
      : menuItems,
  };
};

export default reports;
